import { useState, useEffect, useMemo, useRef } from "react";
import { message } from "antd";
import db from "../../database";
import useAuthStore from "../../stores/useAuthStore";
import usePOSConfigStore from "../../stores/usePOSConfigStore";
import useAdvanceOrderStore from "../../stores/useAdvanceOrderStore";
import { truncateToPrecision, isFractionBlocked } from "./productPopupUtils";
import { readScaleWeight, getScaleUrl, isScaleConfigured, isScaleOnlyProduct, getScaleNotice, SCALE_FAILS_BEFORE_MESSAGE } from "../../utils/weighingScale";

// How long the weight stays green after the scale comes back.
export const SCALE_RECOVERED_MS = 2000;
import { hasBrowserScale } from "../scale/readScaleWeight";

/**
 * Weight entry, UOM conversion, and scale polling logic for ProductsPopup.
 *
 * @param {Object} params
 * @param {Object}   params.selectedProduct
 * @param {Function} params.setSelectedProduct
 * @param {Function} params.selectedProductCallback
 * @param {boolean}  params.weightPopup
 * @param {Function} params.setWeightPopup
 * @param {Object}   params.selectedProductInCart
 * @param {Function} params.setSelectedProductInCart
 */
const useProductWeight = ({ selectedProduct, setSelectedProduct, selectedProductCallback, weightPopup, setWeightPopup, selectedProductInCart, setSelectedProductInCart }) => {
  const posConfig = usePOSConfigStore((s) => s.posConfig);
  const isAdvanceOrderEnabled = useAdvanceOrderStore.getState().isAdvanceOrderEnabled;
  const isPreOrderPage = window.location.pathname.includes("/preorder");
  const tillData = useAuthStore.getState().tillData;

  const [isFirstInput, setIsFirstInput] = useState(true);
  const [unit, setUnit] = useState("kg");
  const [hasDecimal, setHasDecimal] = useState(false);
  const weightInputRef = useRef(null);

  // --- UOM state ---
  const [uomPopup, setUomPopup] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [selectedUomId, setSelectedUomId] = useState(null);
  const [uomQuantity, setUomQuantity] = useState("1");
  const [isUomLocked, setIsUomLocked] = useState(false);
  const uomInputRef = useRef(null);
  const uomConfirmedRef = useRef(false);

  const [availableUoms, setAvailableUoms] = useState([]);

  // FIX: Refs so interval callbacks always read the latest values (not stale closures)
  const availableUomsRef = useRef([]);
  const selectedUomIdRef = useRef(null);
  const uomPopupRef = useRef(false);
  const weightPopupRef = useRef(false);
  const isPollingRef = useRef(false);
  // BUG-2568: why the scale gives no weight, shown in the popup after a few failed reads in a row.
  const [scaleStatus, setScaleStatus] = useState(null);
  const scaleFailCountRef = useRef(0);
  // The product in the open popup can only be weighed (restrictPosQtyEdit "Y"); the poll reads it here.
  const scaleOnlyRef = useRef(false);
  // The cashier pressed refresh: tell them why no weight came, whatever the product.
  const scaleAskedRef = useRef(false);
  // A scale message is on screen; the next good read turns the weight green briefly.
  const scaleShownRef = useRef(false);
  const [scaleRecovered, setScaleRecovered] = useState(false);
  const recoveredTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(recoveredTimerRef.current), []);

  // FIX: Keep ALL refs always in sync with their state counterparts
  useEffect(() => {
    selectedUomIdRef.current = selectedUomId;
  }, [selectedUomId]);

  useEffect(() => {
    uomPopupRef.current = uomPopup;
  }, [uomPopup]);

  useEffect(() => {
    weightPopupRef.current = weightPopup;
  }, [weightPopup]);

  useEffect(() => {
    scaleOnlyRef.current = isScaleOnlyProduct(uomPopup ? pendingProduct : selectedProduct);
  }, [uomPopup, pendingProduct, selectedProduct]);

  // Detect alternate-UOM products and open UOM popup
  useEffect(() => {
    if (uomConfirmedRef.current) {
      uomConfirmedRef.current = false;
      return;
    }

    if (selectedProduct?.alternateUom?.length > 0 && Array.isArray(selectedProduct?.alternateUom) && selectedProduct?.showAlternateUomInPos === "Y") {
      setPendingProduct(selectedProduct);
      setUomPopup(true);
      setWeightPopup(false);
    }
  }, [selectedProduct]);

  // Build initial availableUoms from pendingProduct (sync, no DB queries)
  useEffect(() => {
    if (!pendingProduct) {
      setAvailableUoms([]);
      return;
    }

    const uoms = [];
    const addedUomIds = new Set();

    if (pendingProduct.uomData && pendingProduct.uomData.length > 0) {
      const primaryUom = pendingProduct.uomData[0];
      uoms.push({
        csUomId: primaryUom.csUomId,
        name: primaryUom.name,
        ediCode: primaryUom.ediCode || primaryUom.symbol,
        isPrimary: true,
        decimal: primaryUom.decimal,
        stdprecision: primaryUom.stdprecision,
        convertionRate: 1,
      });
      addedUomIds.add(primaryUom.csUomId);
    } else if (pendingProduct.uom) {
      uoms.push({
        csUomId: pendingProduct.uom,
        name: pendingProduct.uom_name || pendingProduct.uomName || "Primary",
        ediCode: pendingProduct.uom_name || pendingProduct.uomName || "",
        isPrimary: true,
        decimal: pendingProduct.isDecimalQty ? "Y" : "N",
        // BUG-1864: stdprecision 0 (unit-based product) is valid — `||` would wrongly fall back to 2.
        stdprecision: pendingProduct.isQtyDesimal ?? 2,
        convertionRate: 1,
      });
      addedUomIds.add(pendingProduct.uom);
    }

    if (pendingProduct.alternateUom && pendingProduct.alternateUom.length > 0) {
      pendingProduct.alternateUom.forEach((altUom) => {
        if (addedUomIds.has(altUom.csUomId)) {
          return;
        }
        uoms.push({
          csUomId: altUom.csUomId,
          name: altUom.name,
          ediCode: altUom.ediCode,
          isPrimary: false,
          convertionRate: altUom.convertionRate,
          decimal: "N",
          stdprecision: 0,
        });
        addedUomIds.add(altUom.csUomId);
      });
    }

    setAvailableUoms(uoms);
  }, [pendingProduct]);

  // Enrich alternate UOMs with actual decimal/stdprecision from db.productUom
  useEffect(() => {
    if (!pendingProduct?.alternateUom?.length) return;

    const altUomIds = pendingProduct.alternateUom.map((u) => u.csUomId);

    const enrichAlternateUoms = async () => {
      try {
        const uomRecords = await db.productUom.where("csUomId").anyOf(altUomIds).toArray();
        if (!uomRecords || uomRecords.length === 0) return;

        const uomMap = {};
        uomRecords.forEach((rec) => {
          uomMap[rec.csUomId] = rec;
        });

        setAvailableUoms((prev) =>
          prev.map((uom) => {
            if (uom.isPrimary || !uomMap[uom.csUomId]) return uom;
            const dbUom = uomMap[uom.csUomId];
            return {
              ...uom,
              decimal: dbUom.decimal ?? uom.decimal,
              stdprecision: dbUom.stdprecision ?? uom.stdprecision,
            };
          })
        );
      } catch (error) {
        console.error("Error enriching alternate UOM precision from db:", error);
      }
    };

    enrichAlternateUoms();
  }, [pendingProduct]);

  // FIX: Keep availableUomsRef always in sync with the latest computed availableUoms
  useEffect(() => {
    availableUomsRef.current = availableUoms;
  }, [availableUoms]);

  const selectedUom = useMemo(() => {
    return availableUoms.find((uom) => uom.csUomId === selectedUomId);
  }, [availableUoms, selectedUomId]);

  const uomPrice = useMemo(() => {
    if (!pendingProduct || !selectedUomId) {
      return pendingProduct?.sunitprice || 0;
    }

    const basePrice = pendingProduct.sunitprice || 0;

    if (Array.isArray(pendingProduct.priceList) && pendingProduct.priceList.length > 0) {
      const matchedPrice = pendingProduct.priceList.find((p) => p.uomId === selectedUomId);
      if (matchedPrice) {
        return matchedPrice.pricestd || matchedPrice.pricelist || basePrice;
      }
    }

    const selectedUomObj = availableUoms.find((uom) => uom.csUomId === selectedUomId);
    if (selectedUomObj && !selectedUomObj.isPrimary && selectedUomObj.convertionRate) {
      return basePrice * Number(selectedUomObj.convertionRate);
    }

    return basePrice;
  }, [pendingProduct, selectedUomId, availableUoms]);

  const uomTotalPrice = useMemo(() => {
    const qty = Number.parseFloat(uomQuantity) || 0;
    const prcPrecision = tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision ?? 2;
    return (uomPrice * qty).toFixed(prcPrecision);
  }, [uomPrice, uomQuantity]);

  // Reset UOM form when popup opens
  useEffect(() => {
    if (uomPopup && pendingProduct) {
      setIsUomLocked(false);
      const primaryUomId = pendingProduct.uom || pendingProduct.uomData?.[0]?.csUomId;
      setSelectedUomId(primaryUomId);
      setIsFirstInput(true);
      const existingQty = pendingProduct.weight ?? pendingProduct.rawWeight ?? "0";
      const selectedUomObj = availableUoms.find((u) => u.csUomId === primaryUomId);
      const isKg = selectedUomObj?.decimal === "Y";
      setUomQuantity(existingQty && Number(existingQty) > 0 ? existingQty.toString() : isKg ? "0" : "1");
    }
  }, [uomPopup, pendingProduct, availableUoms]);

  // Focus UOM input — initial focus + retention interval to prevent background focus theft
  useEffect(() => {
    if (!uomPopup) return;

    const focusUomInput = () => {
      if (uomInputRef.current) {
        // Only re-focus if focus has drifted outside the UOM popup modal
        const active = document.activeElement;
        const popupContainer = uomInputRef.current?.input?.closest?.(".ant-modal") || uomInputRef.current?.closest?.(".ant-modal");
        const isFocusInsidePopup = popupContainer && popupContainer.contains(active);
        if (!isFocusInsidePopup) {
          uomInputRef.current.focus();
          uomInputRef.current.select?.();
        }
      }
    };

    // Initial focus
    setTimeout(focusUomInput, 100);
    // Retention: re-check every 2s while popup is open
    const interval = setInterval(focusUomInput, 2000);
    return () => clearInterval(interval);
  }, [uomPopup, selectedUomId]);

  // Focus weight input — initial focus + retention interval to prevent background focus theft
  useEffect(() => {
    if (!weightPopup) return;

    // BUG-1192: Skip focus for disabled inputs
    const isDisabled = !isAdvanceOrderEnabled && !isPreOrderPage && selectedProduct?.restrictPosQtyEdit === "Y";
    if (isDisabled) return;

    const focusInput = () => {
      if (weightInputRef.current) {
        // Only re-focus if focus has drifted outside the weight popup modal
        const active = document.activeElement;
        const popupContainer = weightInputRef.current?.input?.closest?.(".ant-modal") || weightInputRef.current?.closest?.(".ant-modal");
        const isFocusInsidePopup = popupContainer && popupContainer.contains(active);
        if (!isFocusInsidePopup) {
          weightInputRef.current.focus?.();
          weightInputRef.current.select?.();
        }
      }
    };

    // Initial focus
    focusInput();
    setTimeout(focusInput, 150);
    // Retention: re-check every 2s while popup is open
    const interval = setInterval(focusInput, 2000);
    return () => clearInterval(interval);
  }, [weightPopup]);

  const handleUomInputChange = (value) => {
    if (isUomLocked || (!isAdvanceOrderEnabled && !isPreOrderPage && selectedProduct?.restrictPosQtyEdit === "Y" && selectedUom?.name?.toLowerCase() === "kilogram")) return;
    const isDecimal = selectedUom?.decimal === "Y";
    let sanitizedValue = value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");

    // FIX: Strip decimals for non-decimal UOMs (Pieces must always be whole numbers)
    if (!isDecimal) {
      sanitizedValue = sanitizedValue.replace(/\./g, "");
    }

    setUomQuantity(sanitizedValue === "" ? "0" : sanitizedValue);
    setIsFirstInput(false);
  };

  const handleUomConfirm = async (overrideCallback) => {
    const qty = Number.parseFloat(uomQuantity) || 1;

    if (qty <= 0) {
      message.error("Quantity must be greater than 0");
      return;
    }

    let newUomData = pendingProduct.uomData;

    if (selectedUomId) {
      try {
        const uomFromDb = await db.productUom.where("csUomId").equals(selectedUomId).toArray();
        if (uomFromDb && uomFromDb.length > 0) {
          newUomData = uomFromDb;
        }
      } catch (error) {
        console.error("Error fetching uomData from db:", error);
        newUomData = pendingProduct.uomData;
      }
    }

    // Build updated product with new UOM, uomData and price
    const uomLabel = selectedUom?.name || newUomData[0]?.name || "";
    const baseName = pendingProduct?.baseName || pendingProduct?.name || "";
    const updatedProduct = {
      ...pendingProduct,
      baseName, // preserve original name for future UOM switches
      name: uomLabel ? `${baseName} (${uomLabel})` : baseName,
      uom: selectedUomId,
      uom_name: selectedUom?.name,
      uomData: newUomData,
      primaryUomId: pendingProduct?.primaryUomId || pendingProduct?.uom || pendingProduct?.uomData?.[0]?.csUomId || null,
      weight: qty,
      isQtyDesimal: newUomData[0]?.stdprecision ?? 2,
      rawWeight: uomQuantity,
      unit: selectedUom?.name?.toLowerCase() || pendingProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
      sunitprice: uomPrice,
      price: uomPrice,
      salePrice: uomPrice,
      realPrice: uomPrice,
    };

    uomConfirmedRef.current = true;

    setUomPopup(false);
    setPendingProduct(null);
    setSelectedUomId(null);
    if (typeof overrideCallback === "function") {
      overrideCallback(updatedProduct);
    } else {
      selectedProductCallback(updatedProduct);
    }
    setIsUomLocked(false);
    setUomQuantity("0");
    setSelectedProduct(updatedProduct);
  };

  const handleUomCancel = () => {
    // Caller provides resetModalState — we just close our own state
    setPendingProduct(null);
    setSelectedUomId(null);
    setUomQuantity("0");
    setIsUomLocked(false);
    setSelectedProduct({});
    setWeightPopup(false);
    setUomPopup(false);
  };

  // Weight-popup init — only run on popup open, not on every selectedProduct change
  useEffect(() => {
    if (weightPopup) {
      setIsFirstInput(true);
      setHasDecimal(false);
      if (!selectedProduct.weight) {
        setSelectedProduct({
          ...selectedProduct,
          weight: "0",
          rawWeight: "0",
        });
      }
      if (posConfig?.predefined_weight_buttons === "Y" && !unit) {
        setUnit(selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg");
      }
    }
  }, [weightPopup, selectedProduct]);

  // --- Numpad / input handlers ---
  const handleTotalQty = (value) => {
    if (!isAdvanceOrderEnabled && !isPreOrderPage && selectedProduct?.restrictPosQtyEdit === "Y" && weightPopup) {
      return;
    }
    if (Object.keys(selectedProduct).length === 0 || selectedProduct.isReturn) return;

    let newWeight = selectedProduct.weight ? selectedProduct.weight.toString() : "0";
    const isPredefinedButtonsEnabled = posConfig?.predefined_weight_buttons === "Y";
    let displayWeight = isPredefinedButtonsEnabled && unit === "g" ? (Number(newWeight) * 1000).toString() : newWeight;
    const currentHasDecimal = displayWeight.includes(".");

    switch (value) {
      case "x":
        displayWeight = displayWeight.slice(0, -1) || "0";
        setHasDecimal(displayWeight.includes("."));
        break;
      case "clear":
        displayWeight = "0";
        setIsFirstInput(true);
        setHasDecimal(false);
        break;
      case ".":
        if (!currentHasDecimal && displayWeight.length !== "0" && (unit === "kg" || !isPredefinedButtonsEnabled)) {
          displayWeight = `${displayWeight}.`;
          setHasDecimal(true);
        }
        break;
      default:
        if (isFirstInput && displayWeight === "0") {
          displayWeight = value;
          setIsFirstInput(false);
        } else {
          displayWeight = `${displayWeight}${value}`;
        }
        setHasDecimal(displayWeight.includes("."));
        break;
    }

    newWeight = isPredefinedButtonsEnabled && unit === "g" ? (Number(displayWeight) / 1000).toString() : displayWeight;

    setSelectedProduct({
      ...selectedProduct,
      weight: newWeight,
      rawWeight: displayWeight,
      unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
    });
  };

  const handleInputChange = (value) => {
    if (!isAdvanceOrderEnabled && !isPreOrderPage && selectedProduct?.restrictPosQtyEdit === "Y" && weightPopup) {
      return;
    }
    if (Object.keys(selectedProduct).length === 0) return;

    const isPredefinedButtonsEnabled = posConfig?.predefined_weight_buttons === "Y";
    const isDecimal = selectedProduct?.uomData?.[0]?.decimal === "Y";
    let sanitizedValue = value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
    if (isPredefinedButtonsEnabled && unit === "g" && !isDecimal) {
      sanitizedValue = sanitizedValue.replace(/\./g, "");
    }
    let newWeight = sanitizedValue === "" ? "0" : sanitizedValue;
    if (isPredefinedButtonsEnabled && unit === "g") {
      newWeight = (Number(sanitizedValue) / 1000).toString();
    }

    setSelectedProduct({
      ...selectedProduct,
      weight: newWeight,
      rawWeight: sanitizedValue,
      unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
    });
    setHasDecimal(newWeight.includes("."));
    setIsFirstInput(false);
  };

  // --- Scale polling ---
  // FIX: callGetWeightAPI reads EVERYTHING from refs — zero stale closure values
  const callGetWeightAPI = async (productDefined) => {
    // Prevent overlapping requests (1s interval, 3s timeout)
    if (isPollingRef.current) return;
    isPollingRef.current = true;
    try {
      return await _callGetWeightAPIInner(productDefined);
    } finally {
      isPollingRef.current = false;
    }
  };

  // BUG-2568: count failed reads in a row; only a till set up with a scale (weighingScale = "Y")
  // is told, so a till with no scale never sees a scale message.
  // The automatic poll tells only for a product that cannot be typed (restrictPosQtyEdit "Y");
  // a press on refresh tells for any product.
  const recordScaleFailure = (reason) => {
    // A read still in flight when the popup closed must not leave a message behind.
    if (!weightPopupRef.current && !uomPopupRef.current) return;
    scaleFailCountRef.current += 1;
    if (!isScaleConfigured(tillData)) return;
    const polledEnough = scaleFailCountRef.current >= SCALE_FAILS_BEFORE_MESSAGE && scaleOnlyRef.current;
    if (polledEnough || scaleAskedRef.current) {
      scaleShownRef.current = true;
      setScaleStatus(getScaleNotice(reason));
    }
  };

  const clearScaleStatus = () => {
    scaleFailCountRef.current = 0;
    scaleAskedRef.current = false;
    scaleShownRef.current = false;
    setScaleStatus(null);
  };

  // A good read after a shown message: clear it and show the weight green for a moment (UX v2).
  const markScaleRecovered = () => {
    const wasShown = scaleShownRef.current;
    clearScaleStatus();
    if (!wasShown) return;
    clearTimeout(recoveredTimerRef.current);
    setScaleRecovered(true);
    recoveredTimerRef.current = setTimeout(() => setScaleRecovered(false), SCALE_RECOVERED_MS);
  };

  // The popup's refresh button: the cashier asked for a reading.
  const refreshWeight = (productDefined) => {
    scaleAskedRef.current = true;
    return callGetWeightAPI(productDefined);
  };

  const _callGetWeightAPIInner = async (productDefined) => {
    if (isAdvanceOrderEnabled || isPreOrderPage) {
      return;
    }
    const hwUrl = getScaleUrl(tillData);
    // CW Browser reads the scale over IPC, so it has no use for the controller URL.
    if (!hwUrl && !hasBrowserScale()) {
      recordScaleFailure("noUrl");
      return;
    }
    if (weightPopup && (!isAdvanceOrderEnabled || !isPreOrderPage)) {
      setIsUomLocked(false);
    }
    // Read ALL live values from refs — these are always current, never stale
    const liveUomId = selectedUomIdRef.current;
    const liveUoms = availableUomsRef.current;
    const liveUomPopup = uomPopupRef.current;
    const liveWeightPopup = weightPopupRef.current;

    const currentUom = liveUoms.find((uom) => uom.csUomId === liveUomId);
    // isKgUom is true ONLY when decimal === "Y" (Kilogram), false for Pieces/any non-decimal
    const isKgUom = currentUom?.decimal === "Y";

    const result = await readScaleWeight(hwUrl, { method: "POST" });
    if (!result.ok) {
      if (result.reason === "invalid") setUomQuantity("0");
      recordScaleFailure(result.reason);
      return;
    }
    markScaleRecovered();

    const { weight } = result;
    const productToUpdate = productDefined && Object.keys(productDefined)?.length > 0 ? productDefined : selectedProductInCart;
    try {
      if (uomPopup && weight > 0) {
        setIsUomLocked(true);
      } else if (uomPopup && weight === 0) {
        setIsUomLocked(false);
      }
      // CASE 1: Weight popup only (no UOM popup open) — always write weight
      if (liveWeightPopup && !liveUomPopup) {
        // BUG-2451: non-decimal UOM (Numbers/Pieces) -- NEVER write scale weight, same hard
        // stop as the UOM popup below. A scale reading here became a fractional qty that the
        // cart stored as 0 but priced at the fraction.
        if (isFractionBlocked(selectedProduct) || isFractionBlocked(productToUpdate)) {
          return;
        }
        if (weight !== parseFloat(selectedProduct.weight)) {
          productToUpdate.weight = parseFloat(truncateToPrecision(weight, currentUom?.stdprecision));
          productToUpdate.rawWeight = parseFloat(truncateToPrecision(weight.toString(), currentUom?.stdprecision));
          productToUpdate.unit = unit || productToUpdate?.uomData?.[0]?.name?.toLowerCase() || "kg";
          setSelectedProductInCart({ ...productToUpdate });
          const updatedProduct = {
            ...selectedProduct,
            weight: parseFloat(truncateToPrecision(weight, currentUom?.stdprecision)),
            rawWeight: parseFloat(truncateToPrecision(weight.toString(), currentUom?.stdprecision)),
            unit: unit || productToUpdate?.uomData?.[0]?.name?.toLowerCase() || "kg",
          };
          setSelectedProduct(updatedProduct);
          setHasDecimal(weight.toString().includes("."));
        }
        return;
      }

      // CASE 2: UOM popup is open
      if (liveUomPopup) {
        // HARD STOP: Pieces or any non-decimal UOM selected — NEVER write scale weight
        if (!isKgUom) {
          return;
        }
        // Only reaches here when Kilogram is selected
        const precision = currentUom?.decimal === "Y" ? 3 : 0;
        setUomQuantity(truncateToPrecision(weight, precision));
        return;
      }
    } catch (error) {
      console.error("BUG-2568: could not apply the scale reading", error);
    }
  };

  // FIX: useEffect restarts interval whenever any popup state or UOM changes.
  // callGetWeightAPI reads everything from refs so interval callback is never stale.
  useEffect(() => {
    let interval;

    // Use refs for current live values at the time this effect runs
    const liveUomId = selectedUomIdRef.current;
    const liveUoms = availableUomsRef.current;
    const currentUom = liveUoms.find((uom) => uom.csUomId === liveUomId);
    const isKgUom = currentUom?.decimal === "Y";

    // Only poll if: weight popup is open OR (uom popup is open AND kilogram is selected)
    const shouldPoll = !isUomLocked && (weightPopup || (uomPopup && isKgUom));

    if (shouldPoll) {
      callGetWeightAPI(selectedProduct);
      interval = setInterval(() => {
        callGetWeightAPI(selectedProduct);
      }, 1000);
    }

    return () => clearInterval(interval);
    // All three in deps: restarts interval whenever any of these change
  }, [weightPopup, uomPopup, selectedUomId, availableUoms]);

  // BUG-2568: each popup opening starts with no scale message and a fresh failure count.
  useEffect(() => {
    if (!weightPopup && !uomPopup) clearScaleStatus();
  }, [weightPopup, uomPopup]);

  const allFrequentWeights = unit === "kg" ? [0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0, 2.5, 3.0, 4.0, 5.0] : [250, 500, 750, 1000, 1250, 1500, 1750, 2000, 2500, 3000, 4000, 5000];
  // BUG-2451: non-decimal UOM -- offer only the presets that are a whole quantity.
  const fractionBlocked = isFractionBlocked(selectedProduct);
  const frequentWeights = fractionBlocked ? allFrequentWeights.filter((w) => Number.isInteger(unit === "g" ? w / 1000 : w)) : allFrequentWeights;

  const handleFrequentWeight = (weight) => {
    const weightInKg = unit === "g" ? weight / 1000 : weight;
    if (fractionBlocked && !Number.isInteger(weightInKg)) return;
    setSelectedProduct({
      ...selectedProduct,
      weight: weightInKg.toString(),
      rawWeight: unit === "g" ? weight.toString() : weightInKg.toString(),
      unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
    });
    setHasDecimal(weightInKg.toString().includes("."));
  };

  const resetWeight = () => {
    setWeightPopup(false);
    setUomPopup(false);
    setPendingProduct(null);
    setSelectedUomId(null);
    setUomQuantity("1");
  };

  return {
    // Weight state
    isFirstInput,
    unit,
    setUnit,
    hasDecimal,
    weightInputRef,

    // UOM state
    uomPopup,
    setUomPopup,
    pendingProduct,
    setPendingProduct,
    selectedUomId,
    setSelectedUomId,
    uomQuantity,
    setUomQuantity,
    isUomLocked,
    setIsUomLocked,
    uomInputRef,
    availableUoms,
    selectedUom,
    uomPrice,
    uomTotalPrice,

    // Handlers
    handleUomInputChange,
    handleUomConfirm,
    handleUomCancel,
    handleTotalQty,
    handleInputChange,
    callGetWeightAPI,
    refreshWeight,
    scaleStatus,
    scaleRecovered,
    handleFrequentWeight,
    frequentWeights,
    resetWeight,
  };
};

export default useProductWeight;
